import { useEffect } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Checkbox, Input } from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { OptionNumberField } from './commitment-fields';
import { PriceRangesList } from './price-ranges-list';
import { TemplateSection, TemplateStack } from './template-shell';
import type { ContractFormValues } from '../contract-form';

/**
 * Config.Config template (generic `Config` group). One config option with a
 * package parameter, unit, price-per-unit and an optional monthly-statement
 * toggle; when statementable, a tiered price list is shown. Writes into
 * `products.{i}.service_configoptions[0]`.
 */
export function ConfigGenericTemplate({ index }: { index: number }) {
  const { control, setValue, getValues } = useFormContext<ContractFormValues>();
  const optsPath = `products.${index}.service_configoptions` as const;
  const options = useWatch({ control, name: optsPath });
  const optPath = `${optsPath}.0` as const;

  const statementable = (useWatch({ control, name: `${optPath}.statementable` as never }) as unknown) === true;
  const commitmentValue = Number(useWatch({ control, name: `${optPath}.commitment_value` as never }) ?? 0);

  useEffect(() => {
    const current = getValues(optsPath);
    if (Array.isArray(current) && current.length > 0) return;
    setValue(
      optsPath,
      [
        {
          name: 'config',
          unit: 'qty',
          commitment_value: 0,
          commitment_price_per_unit: 0,
          resizable: true,
          statementable: false,
          prices: [],
        },
      ],
      { shouldDirty: true },
    );
  }, [optsPath, getValues, setValue]);

  const onStatementableChange = (checked: boolean) => {
    setValue(`${optPath}.statementable` as never, checked as never, { shouldDirty: true });
    setValue(
      `${optPath}.prices` as never,
      (checked ? [{ price_start: 0, price_end: 999999999, price_per_unit: 0 }] : []) as never,
      { shouldDirty: true },
    );
  };

  if (!Array.isArray(options) || options.length === 0) return null;

  return (
    <TemplateStack>
      <TemplateSection title="Configuration">
        <div style={{ display: 'grid', gap: 'var(--vn-space-3)', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
          <OptionNumberField path={`${optPath}.commitment_value`} label="Package parameters" />
          <Controller
            control={control}
            name={`${optPath}.unit` as never}
            render={({ field }) => (
              <Field label="Unit of calculation">
                {(p) => (
                  <Input
                    {...p}
                    value={(field.value as string) ?? ''}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    name={field.name}
                    ref={field.ref}
                  />
                )}
              </Field>
            )}
          />
          <OptionNumberField path={`${optPath}.commitment_price_per_unit`} label="Price per unit" />
        </div>
        <Field label="Monthly statement">
          {() => (
            <Checkbox
              checked={statementable}
              onCheckedChange={(v) => onStatementableChange(v === true)}
            />
          )}
        </Field>
      </TemplateSection>

      {statementable && (
        <TemplateSection title="Pricing configuration">
          <PriceRangesList name={`${optPath}.prices`} commitmentValue={commitmentValue} />
        </TemplateSection>
      )}
    </TemplateStack>
  );
}
